import { useState, useEffect } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, TrendingUp, DollarSign, Users, BarChart3, Loader2 } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const emptyFund = { name: '', description: '', risk_level: 'moderate', min_investment: 100, target_return: 0, management_fee: 0, investor_share_pct: 50, company_share_pct: 50, status: 'active' };
const RISK_COLORS = { conservative: 'bg-blue-100 text-blue-700', moderate: 'bg-amber-100 text-amber-700', aggressive: 'bg-red-100 text-red-700' };
const CHART_TICK_SM = { fontSize: 10 };

export default function FundSchemesPage() {
  const [funds, setFunds] = useState([]);
  const [selectedFund, setSelectedFund] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyFund);
  const [navForm, setNavForm] = useState({ fund_id: '', nav_value: '', date: new Date().toISOString().slice(0, 10), notes: '' });
  const [navDialogOpen, setNavDialogOpen] = useState(false);
  const [allocForm, setAllocForm] = useState({ client_id: '', fund_id: '', amount: '' });
  const [allocDialogOpen, setAllocDialogOpen] = useState(false);
  const [clients, setClients] = useState([]);

  const fetchData = async () => {
    try {
      const [fRes, cRes] = await Promise.all([api.get('/funds-schemes'), api.get('/clients').catch(() => ({ data: [] }))]);
      setFunds(fRes.data); setClients(cRes.data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const loadFundDetail = async (id) => {
    try { const { data } = await api.get(`/funds-schemes/${id}`); setSelectedFund(data); } catch (err) { console.error('[FundSchemes] load detail failed:', err?.message || err); }
  };

  const handleCreateFund = async () => {
    try { await api.post('/funds-schemes', form); toast.success('Fund created'); setDialogOpen(false); setForm(emptyFund); fetchData(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const handleRecordNav = async () => {
    try { await api.post('/funds-schemes/nav', { ...navForm, nav_value: parseFloat(navForm.nav_value) }); toast.success('NAV recorded'); setNavDialogOpen(false); if (selectedFund) loadFundDetail(selectedFund.id); fetchData(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const handleAllocate = async () => {
    try { await api.post('/funds-schemes/allocate', { ...allocForm, amount: parseFloat(allocForm.amount) }); toast.success('Allocation done'); setAllocDialogOpen(false); if (selectedFund) loadFundDetail(selectedFund.id); fetchData(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const totalAum = funds.reduce((s, f) => s + (f.total_aum || 0), 0);

  return (
    <div className="space-y-6" data-testid="fund-schemes-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-brand-text-primary tracking-tight">Fund Schemes</h1>
          <p className="text-sm text-brand-text-secondary mt-1">Manage investment funds, NAV, and client allocations</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { setForm(emptyFund); setDialogOpen(true); }} className="bg-brand-primary text-white rounded-lg" data-testid="create-fund-btn"><Plus size={16} className="mr-1" /> New Fund</Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { l: 'Total Funds', v: funds.length, icon: BarChart3, c: 'text-brand-primary' },
          { l: 'Total AUM', v: `$${totalAum.toLocaleString()}`, icon: DollarSign, c: 'text-brand-success' },
          { l: 'Active Funds', v: funds.filter(f => f.status === 'active').length, icon: TrendingUp, c: 'text-brand-accent' },
          { l: 'Total Investors', v: funds.reduce((s, f) => s + (f.total_investors || 0), 0), icon: Users, c: 'text-brand-secondary' },
        ].map(s => (
          <Card key={s.l} className="border border-brand-border bg-white metric-card">
            <CardContent className="p-4 flex items-center gap-3"><div className="w-9 h-9 rounded-lg bg-brand-bg flex items-center justify-center"><s.icon size={16} className={s.c} /></div><div><p className="text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">{s.l}</p><p className={`font-heading text-xl font-bold ${s.c}`}>{s.v}</p></div></CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Fund Cards */}
        <div className="lg:col-span-1 space-y-3">
          {loading ? <p className="text-sm text-brand-text-secondary">Loading...</p> :
           funds.length === 0 ? <p className="text-sm text-brand-text-secondary">No funds created yet.</p> :
           funds.map(f => (
            <Card key={f.id} className={`border cursor-pointer transition-all hover:shadow-md ${selectedFund?.id === f.id ? 'border-brand-primary bg-brand-primary/5' : 'border-brand-border bg-white'}`} onClick={() => loadFundDetail(f.id)} data-testid={`fund-card-${f.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-heading font-semibold text-brand-text-primary text-sm">{f.name}</h3>
                  <Badge className={`text-[10px] border-0 ${RISK_COLORS[f.risk_level] || ''}`}>{f.risk_level}</Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div><p className="text-brand-text-secondary">NAV</p><p className="font-semibold text-brand-primary">{f.current_nav}</p></div>
                  <div><p className="text-brand-text-secondary">AUM</p><p className="font-semibold">${(f.total_aum || 0).toLocaleString()}</p></div>
                  <div><p className="text-brand-text-secondary">Investors</p><p className="font-semibold">{f.total_investors || 0}</p></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Fund Detail */}
        <div className="lg:col-span-2">
          {selectedFund ? (
            <Card className="border border-brand-border bg-white">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading text-lg text-brand-text-primary">{selectedFund.name}</CardTitle>
                    <p className="text-xs text-brand-text-secondary mt-0.5">{selectedFund.description}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setNavForm({ fund_id: selectedFund.id, nav_value: '', date: new Date().toISOString().slice(0, 10), notes: '' }); setNavDialogOpen(true); }} className="rounded-lg text-xs" data-testid="record-nav-btn">Record NAV</Button>
                    <Button size="sm" onClick={() => { setAllocForm({ client_id: '', fund_id: selectedFund.id, amount: '' }); setAllocDialogOpen(true); }} className="bg-brand-primary text-white rounded-lg text-xs" data-testid="allocate-btn">Allocate Client</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <Tabs defaultValue="overview" className="mt-3">
                  <TabsList className="bg-brand-bg/50 border border-brand-border/50 rounded-lg p-0.5">
                    {['overview', 'nav', 'investors'].map(t => <TabsTrigger key={t} value={t} className="rounded-md text-xs px-3 py-1 capitalize data-[state=active]:bg-white data-[state=active]:text-brand-primary">{t}</TabsTrigger>)}
                  </TabsList>
                  <TabsContent value="overview" className="mt-3">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        { l: 'Current NAV', v: selectedFund.current_nav || 100 },
                        { l: 'Total AUM', v: `$${(selectedFund.total_aum || 0).toLocaleString()}` },
                        { l: 'Investors', v: selectedFund.total_investors || 0 },
                        { l: 'Target Return', v: `${selectedFund.target_return || 0}%` },
                      ].map(s => <div key={s.l} className="bg-brand-bg/60 rounded-lg p-3 border border-brand-border/50"><p className="text-[10px] uppercase text-brand-text-secondary font-semibold">{s.l}</p><p className="font-heading text-lg font-bold text-brand-text-primary">{s.v}</p></div>)}
                    </div>
                  </TabsContent>
                  <TabsContent value="nav" className="mt-3">
                    {selectedFund.nav_history?.length > 0 ? (
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={[...selectedFund.nav_history].reverse()}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                            <XAxis dataKey="date" tick={CHART_TICK_SM} />
                            <YAxis tick={CHART_TICK_SM} />
                            <Tooltip />
                            <Area type="monotone" dataKey="nav_value" stroke="#2563EB" fill="#2563EB" fillOpacity={0.1} strokeWidth={2} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    ) : <p className="text-sm text-brand-text-secondary py-4">No NAV history yet. Record NAV to see chart.</p>}
                  </TabsContent>
                  <TabsContent value="investors" className="mt-3">
                    {selectedFund.allocations?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead><tr className="border-b border-brand-border">{['Client', 'Units', 'Invested', 'Current', 'Gain', 'NAV'].map(h => <th key={h} className="text-left py-2 px-3 text-xs uppercase font-semibold text-brand-text-secondary">{h}</th>)}</tr></thead>
                          <tbody>{selectedFund.allocations.map(a => (
                            <tr key={a.id} className="border-b border-brand-border/50">
                              <td className="py-2 px-3 font-medium text-brand-text-primary text-xs">{a.client_name} <span className="text-brand-primary font-mono">({a.client_code})</span></td>
                              <td className="py-2 px-3 text-xs">{a.units}</td>
                              <td className="py-2 px-3 text-xs">${(a.invested_amount || 0).toLocaleString()}</td>
                              <td className="py-2 px-3 text-xs font-medium">${(a.current_value || 0).toLocaleString()}</td>
                              <td className={`py-2 px-3 text-xs font-medium ${(a.unrealized_gain || 0) >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>${(a.unrealized_gain || 0).toLocaleString()}</td>
                              <td className="py-2 px-3 text-xs">{a.current_nav}</td>
                            </tr>
                          ))}</tbody>
                        </table>
                      </div>
                    ) : <p className="text-sm text-brand-text-secondary py-4">No investors allocated yet.</p>}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : <div className="flex items-center justify-center h-64 text-sm text-brand-text-secondary">Select a fund to view details</div>}
        </div>
      </div>

      {/* Create Fund Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="create-fund-dialog">
          <DialogHeader><DialogTitle className="font-heading">Create Fund Scheme</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5"><Label className="text-xs">Fund Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Aggressive Growth Fund" className="bg-white border-brand-border rounded-lg" data-testid="fund-name-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} className="bg-white border-brand-border rounded-lg text-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Risk Level</Label>
                <Select value={form.risk_level} onValueChange={v => setForm({...form, risk_level: v})}>
                  <SelectTrigger className="bg-white border-brand-border rounded-lg" data-testid="fund-risk-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="conservative">Conservative</SelectItem><SelectItem value="moderate">Moderate</SelectItem><SelectItem value="aggressive">Aggressive</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Min Investment ($)</Label><Input type="number" value={form.min_investment} onChange={e => setForm({...form, min_investment: parseFloat(e.target.value) || 0})} className="bg-white border-brand-border rounded-lg" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Target Return (%)</Label><Input type="number" value={form.target_return} onChange={e => setForm({...form, target_return: parseFloat(e.target.value) || 0})} className="bg-white border-brand-border rounded-lg" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Mgmt Fee (%)</Label><Input type="number" value={form.management_fee} onChange={e => setForm({...form, management_fee: parseFloat(e.target.value) || 0})} className="bg-white border-brand-border rounded-lg" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">Cancel</Button><Button onClick={handleCreateFund} className="bg-brand-primary text-white rounded-lg" data-testid="save-fund-btn">Create Fund</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NAV Dialog */}
      <Dialog open={navDialogOpen} onOpenChange={setNavDialogOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="nav-dialog">
          <DialogHeader><DialogTitle className="font-heading text-lg">Record NAV</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5"><Label className="text-xs">Date</Label><Input type="date" value={navForm.date} onChange={e => setNavForm({...navForm, date: e.target.value})} className="bg-white border-brand-border rounded-lg" /></div>
            <div className="space-y-1.5"><Label className="text-xs">NAV Value *</Label><Input type="number" step="0.01" value={navForm.nav_value} onChange={e => setNavForm({...navForm, nav_value: e.target.value})} placeholder="100.00" className="bg-white border-brand-border rounded-lg" data-testid="nav-value-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Notes</Label><Input value={navForm.notes} onChange={e => setNavForm({...navForm, notes: e.target.value})} className="bg-white border-brand-border rounded-lg" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setNavDialogOpen(false)} className="rounded-lg">Cancel</Button><Button onClick={handleRecordNav} className="bg-brand-primary text-white rounded-lg" data-testid="save-nav-btn">Record NAV</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Allocate Dialog */}
      <Dialog open={allocDialogOpen} onOpenChange={setAllocDialogOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="allocate-dialog">
          <DialogHeader><DialogTitle className="font-heading text-lg">Allocate Client to Fund</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5"><Label className="text-xs">Client *</Label>
              <Select value={allocForm.client_id} onValueChange={v => setAllocForm({...allocForm, client_id: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-lg" data-testid="alloc-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.client_id} — {c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Investment Amount ($) *</Label><Input type="number" value={allocForm.amount} onChange={e => setAllocForm({...allocForm, amount: e.target.value})} placeholder="1000" className="bg-white border-brand-border rounded-lg" data-testid="alloc-amount-input" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAllocDialogOpen(false)} className="rounded-lg">Cancel</Button><Button onClick={handleAllocate} className="bg-brand-primary text-white rounded-lg" data-testid="save-alloc-btn">Allocate</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
